import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@sandtech-hub-monorepo/ui-kit';
import { TranscriptTerm } from '../utils';
import TermStandingConduct from './TermStandingConduct';

interface Props {
  term: TranscriptTerm;
  semesterGPA: string;
  cumulativeGPA: string;
  /**
   * Academic standing and conduct cases belong to the in-app (unofficial)
   * academic record only — they are never part of the official transcript.
   */
  showStandingConduct?: boolean;
}

const formatGradePoints = (points: number | null): string =>
  points == null ? '—' : points.toFixed(2);

const TermTable = ({
  term,
  semesterGPA,
  cumulativeGPA,
  showStandingConduct = true,
}: Props) => (
  <div className="w-full border border-muted rounded-md overflow-hidden print:overflow-visible print:break-inside-avoid">
    <div className="flex items-center justify-between px-3 py-2 text-xs md:text-sm bg-muted/30">
      <p className="font-semibold text-foreground">{term.term}</p>
      <p className="font-medium text-muted-foreground">
        <span className="hidden sm:inline">Total credits earned: </span>
        <span className="sm:hidden">Credits: </span>
        {term.earnedCredits}
      </p>
    </div>

    <Table>
      <TableHeader>
        <TableRow className="border-muted">
          <TableHead className="whitespace-nowrap font-normal sm:text-xs">
            Course
          </TableHead>
          <TableHead className="hidden md:table-cell print:table-cell w-full font-normal sm:text-xs">
            Course Title
          </TableHead>
          <TableHead className="text-center whitespace-nowrap leading-tight font-normal sm:text-xs">
            Credits
            <br />
            Attempted
          </TableHead>
          <TableHead className="text-center whitespace-nowrap leading-tight font-normal sm:text-xs">
            Credits
            <br />
            Earned
          </TableHead>
          <TableHead className="text-center whitespace-nowrap font-normal sm:text-xs">
            Grade
          </TableHead>
          <TableHead className="text-center whitespace-nowrap leading-tight font-normal sm:text-xs">
            GPA
            <br />
            Points
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {term.courses.map((course, i) => (
          <TableRow key={i} className="border-muted">
            <TableCell className="text-foreground">
              {course.abbreviation}
            </TableCell>
            <TableCell className="hidden md:table-cell print:table-cell text-foreground">
              {course.name}
            </TableCell>
            <TableCell className="text-center text-foreground">
              {course.attemptedCredits}
            </TableCell>
            <TableCell className="text-center text-foreground">
              {course.earnedCredits}
            </TableCell>
            <TableCell className="text-center font-medium text-foreground">
              {course.grade ?? '—'}
            </TableCell>
            <TableCell className="text-center text-foreground">
              {formatGradePoints(course.gradePoints)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter className="bg-muted/30 border-muted/50">
        <TableRow>
          <TableCell className="font-semibold text-foreground">
            Totals
          </TableCell>
          <TableCell className="hidden md:table-cell print:table-cell" />
          <TableCell className="text-center font-medium text-foreground">
            {term.attemptedCredits}
          </TableCell>
          <TableCell className="text-center font-medium text-foreground">
            {term.earnedCredits}
          </TableCell>
          <TableCell colSpan={2} className="text-right align-top">
            <span className="block text-xs md:text-sm whitespace-nowrap">
              <span className="font-medium text-muted-foreground">
                Semester GPA.{' '}
              </span>
              <span className="font-medium text-foreground">{semesterGPA}</span>
            </span>
            <span className="block text-xs md:text-sm whitespace-nowrap">
              <span className="font-medium text-muted-foreground">
                Cumulative GPA.{' '}
              </span>
              <span className="font-medium text-foreground">
                {cumulativeGPA}
              </span>
            </span>
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>

    {showStandingConduct && <TermStandingConduct term={term} />}
  </div>
);

export default TermTable;
